import React, { useState, useMemo, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Table,
    Modal,
    Space,
    Typography
} from 'antd';
import type { PopconfirmProps, TableColumnsType, InputRef } from 'antd';
import {
    SearchOutlined,
    PrinterOutlined,
    DeleteOutlined,
    CaretUpOutlined,
    CaretDownOutlined
} from '@ant-design/icons';

import { apiRequest } from '../../../service/apiRequest';

import { showSuccess, showError } from '../../../components/errorHandler';

import useBillingShortcuts from '../../../hooks/useBillingShortcuts';

import useDebounce from '../../../hooks/useDebounce';
import debounce from 'lodash/debounce';
import AddJobModal from './AddJobModal';

const { Option } = Select;
const { Title } = Typography;
interface clientItem {
    ledger_id: number;
    ledger_first_name: string;
    ledger_last_name: string;
    ledger_mobile_no: string;
    ledger_company_name: string;
    // ledger_company_gst_no: string;
    ledger_gstin: string;
    active_cart: string;
    sale_id: string;
}
interface CartItem {
    sale_id: string;
    sd_id: number;
    sale_invoice_no: string;
    sale_date: string;
    sale_qty: string;
    sale_price: string;
    sale_total: string;
    sale_discount: string;
    sale_tax: string;
    sale_net_total: number;
    sale_category: string;
    sale_handover_name: string;
    sale_handover_mobile: string;
    sale_employee_id: string;
    sale_reference_name: string;
    sale_reference_mobile: string;
    sale_transaction_id: string;
    sale_status: number;


    sd_product_id: number;
    sd_qty: number;
    sd_amount: number;
    sd_discount: number;
    sd_sub_total: number;
    sd_unit: number;
    sd_mtr: number;
    sd_ledger_id: number;
    sd_sale_type: string;
    sd_manual_job_no: string;
    sd_trail_date: string;
    sd_trail_time: string;
    sd_mobile_no: string;
    sd_delivery_date: string;
    sd_delivery_time: string;
    sd_for_custmor: string;

    product_name: string;
    product_code: string;

    sd_sale_id: string;
    
    sd_variant_code: string;
    sd_tailor_name: string;
    sd_measurement: string;
    sd_fabric_issued: string;
}
interface ProductRow {
    sr: number;
    product: string;
    mtr: number;
    qty: number;
    unit: string;
    rate: number;
    discount: number;
    subtotal: number;
    sd_variant_code: string;
    sd_qty: number;
    sd_unit: number;
}
interface Ledger {
    ledger_id: number;
    ledger_deals_in_company?: number;
    ledger_status?: number;
    ledger_first_name: string;
    ledger_last_name?: string;
    ledger_mobile_no?: string;
    ledger_company_name?: string;
    ledger_gstin?: string;
}
const InvoiceUI: React.FC = () => {
    const inputRef = React.useRef<InputRef>(null);
    const [discountModalVisible, setDiscountModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [showBottomSection, setShowBottomSection] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [clientData, setclientData] = useState<clientItem | null>(null);
    const [cartData, setCart] = useState<CartItem[]>([]);
    const [ledgers, setLedgers] = useState<Ledger[]>([]);
    const [dropdownOptions, setDropdownOptions] = useState<{ label: string; value: number }[]>([]);
    const [saleId, setsaleId] = useState<string | null>(null);

    const [jobModalVisible, setJobModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<CartItem | null>(null);





    const [form] = Form.useForm();

    const updateQuantity = (sale_id: string, quantity: number) => {
        if (quantity <= 0) {
            setCart((prev) => prev.filter((item) => item.sale_id !== sale_id));
        } else {
            setCart((prev) =>
                prev.map((item) =>
                    item.sale_id === sale_id ? { ...item, sd_qty: quantity } : item
                )
            );
        }
    };
    const subtotal = Array.isArray(cartData) && cartData.length
        ? cartData.reduce((sum, item) => sum + item.sd_sub_total, 0)
        : 0;
    const clearCart = () => setCart([]);
    const handleInputChange = (value: string | number, rowIndex: number, key: keyof ProductRow) => {
        const newData = [...dataSource];
        const updatedRow = { ...newData[rowIndex], [key]: value };

        // If qty or rate is changed, recalculate subtotal
        if (key === 'qty' || key === 'rate') {
            updatedRow.subtotal = updatedRow.qty * updatedRow.rate;
        }

        newData[rowIndex] = updatedRow;
        setDataSource(newData);
    };
    

    const columns: TableColumnsType<CartItem> = [
        {
            title: '#',
            key: 'index',
            width: 70,
            align: 'center',
            render: (_: any, __: any, index: number) => <div>{index + 1}</div>,
        },
        {
            title: 'Product Details', dataIndex: 'product', key: 'product', width: 500,
            render: (_, record: any) => (
                <span>
                    {record.product_name} ({record.product_code})
                    {record.sd_variant_code ? ` - ${record.sd_variant_code}` : ''}
                </span>
            ),
        },
        {
            title: 'Mtr',
            dataIndex: 'mtr',
            key: 'mtr',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.sd_mtr}
                    onChange={e => handleInputChange(Number(e.target.value), index, 'mtr')}
                />
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            key: 'qty',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.sd_qty}
                    onChange={e => handleInputChange(Number(e.target.value), index, 'qty')}
                />
            ),
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            render: (_, record, index) => (
                <Input
                    value={record.sd_unit}
                    onChange={e => handleInputChange(e.target.value, index, 'unit')}
                />
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.sd_amount}
                    onChange={e => handleInputChange(Number(e.target.value), index, 'rate')}
                />
            ),
        },
        {
            title: 'Discount',
            dataIndex: 'discount',
            key: 'discount',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.sd_discount}
                    onChange={e => handleInputChange(Number(e.target.value), index, 'discount')}
                />
            ),
        },
        {
            title: 'Sub Total',
            dataIndex: 'subtotal',
            key: 'subtotal',
            render: (_, record) => <span>{record.sd_sub_total}</span>,
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: () => (
                <Space>
                    <Button danger icon={<DeleteOutlined />} />
                </Space>
            ),
        },
    ];
    
    useBillingShortcuts();
    useEffect(() => {
        const handleProductSearch = () => {
            console.log('Product search opened (F1)');
            inputRef.current?.focus();
        };

        document.addEventListener('shortcut-open-product', handleProductSearch);
        return () => {
            document.removeEventListener('shortcut-open-product', handleProductSearch);
        };
    }, []);
    // when edit 
    // useEffect(() => {
    //     form.setFieldsValue({
    //         invoiceCategory: 'estimate',
    //         phone: '9999999999',
    //         firstName: 'Anjali',
    //         lastName: 'Sharma',
    //         company: 'XYZ Ltd.',
    //         gst: '11AAAAA0000A1Z5',
    //     });
    // }, []);
    const fetchcart = async (
        sale_id: string | null = null) => {
        try {
            const s_id = saleId;
            const response = await apiRequest<{
                data: CartItem[];
                sale_id: string;
            }>({
                method: 'get',
                url: `/get-to-cart/${s_id}`
            });
            setCart(response.data)
            console.log("data", response.data);
        } catch {
            showError('Failed to fetch vouchers');
        }
    };
    const insertProductToCart = async (searchValue: string) => {
        if (!searchValue) return;
        if (!clientData) {
            showError('Please enter customer details before searching for a product.');
            return;
        }

        try {
            const response = await apiRequest({
                method: 'post',
                url: `/add-to-cart`,
                data: {
                    product_code: searchValue,
                    ledger_id: clientData.ledger_id
                },
            });
            setsaleId(response.sale_id)
            fetchcart();
            
            showSuccess('Product added to cart');
            setSearchText('');
        } catch (error) {
            showError('Product not found or failed to add');
        }
    };
    const [dataSource, setDataSource] = useState<ProductRow[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => ({
        sr: i,
        product: `Product ${i}`,
        mtr: 1,
        qty: 2,
        unit: '',
        rate: 200,
        discount: 2,
        subtotal: 400,
        sd_variant_code: '',
        sd_qty: 2,
        sd_unit: 2
    })));
    const tableScrollY = useMemo(() => {
        return showBottomSection ? 'calc(100vh - 629px)' : 'calc(100vh - 357px)';
    }, [showBottomSection]);

    const totalAmount = useMemo(() => {
        return dataSource.reduce((sum, row) => sum + (row.subtotal || 0), 0);
    }, [dataSource]);

    const checkUser = async (
        sale_id: string | null = null) => {
        let mobile = form.getFieldValue('phone');
        let first_name = form.getFieldValue('firstName');
        let last_name = form.getFieldValue('lastName');
        try {
            const response = await apiRequest<{
                data: clientItem;
                sale_id: string;
            }>({
                method: 'get',
                url: `/check-client?mobile=${mobile}&first_name=${first_name}&last_name=${last_name}`
            });
            console.log("data", response.data);
            if (response.data) {
                setclientData(response.data);
                form.setFieldsValue({
                    firstName: response.data.ledger_first_name.split(' ')[0] || '',
                    lastName: response.data.ledger_last_name || '',
                    company: response.data.ledger_company_name || '',
                    gst: response.data.ledger_gstin || '',
                });
            } else {
            }

        } catch {
            showError('Failed to fetch vouchers');
        }
    };
    const debouncedSearchText = useDebounce(searchText);
    useEffect(() => {
        if (debouncedSearchText.trim() !== '') {
            insertProductToCart(debouncedSearchText.trim());
        }
    }, [debouncedSearchText]);
    useEffect(() => {
        fetchLedger();
    }, []);
    useEffect(() => {
        setsaleId(clientData?.active_cart || null);
    }, [clientData?.active_cart]);
    useEffect(() => {
        if (saleId) {
            fetchcart();
        }
    }, [saleId]);
    const handlePayNow = async () => {
        try {
            // Validate the form
            const formData = await form.validateFields();

            // Prepare payload (basic example)
            const payload = {
                customer: {
                    phone: formData.phone,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    company: formData.company,
                    gst: formData.gst,
                    reference: {
                        phone: formData.refPhone,
                        name: formData.refName,
                    },
                    handover: {
                        phone: formData.handoverPhone,
                        name: formData.handoverName,
                    },
                    invoiceCategory: formData.invoiceCategory,
                    salesEmployee: formData.salesEmployee,
                },
                items: dataSource,
                totalAmount,
            };

            // Make API request
            const response = await apiRequest({
                method: 'post',
                url: '/submit-invoice',
                data: payload,
            });

            showSuccess('Invoice submitted successfully');
            console.log('Response:', response);
            // Optionally reset the form and cart
            form.resetFields();
            setDataSource([]);
            setCart([]);
        } catch (error) {
            showError('Failed to submit invoice');
        }
    };
    const fetchLedger = async (mobile?: string) => {
        try {
            let url = `ledger-data`;
            if (mobile) url += `?mobile=${encodeURIComponent(mobile)}`;

            const response = await apiRequest<{ ledgers: Ledger[] }>({
                method: 'get',
                url,
            });
            console.log("ledger", response);
            const fetched = response.ledgers || [];
            setLedgers(fetched);

            // Map to dropdown options
            const options = fetched.map((ledger) => ({
                label: `${ledger.ledger_first_name} ${ledger.ledger_last_name || ''}`,
                value: ledger.ledger_id,
            }));

            setDropdownOptions(options);

            // Auto-select if only one match
            if (options.length === 1) {
                form.setFieldsValue({
                    handover_full_name: options[0].value,
                });
            } else {
                form.setFieldsValue({ handover_full_name: undefined });
            }

        } catch (error) {
            showError('Failed to fetch Ledger');
            console.error(error);
        }
    };

    const handleCreate = (data: { sale_id: string }) => {
        setsaleId(data.sale_id);
        fetchcart();
    };




    const hideCreateModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };
    const handleEditClick = (record?: CartItem) => {
        if (!record) {
            showError("No cart data available");
            return;
        }

        setIsEditMode(true);
        setInitialValues({
            ...record, // all cart fields including sd_id and ledger_id
        });

        setIsModalVisible(true);
    };

    const showCreateModal = (record?: CartItem) => {
        // if (!record) {
        //     showError("No cart data available");
        //     return;
        // }

        if (!clientData?.ledger_id) {
            showError("Client data not found. Please enter phone number and fetch client first.");
            return;
        }
        
        setIsEditMode(false);
        
        const defaultJobFields = {
            sd_sale_id: record?.sale_id ?? '',  
            sd_ledger_id: clientData.ledger_id,
            sd_product_id: record?.sd_product_id ?? 0,
            sd_sale_type: "job",
        };

        setInitialValues({
        ...record,
        ...defaultJobFields,
        } as CartItem);

        setIsModalVisible(true);
    };
    return (
    <>
        <Form layout="vertical" form={form} onFinish={handlePayNow}>
            {/* Top Row - 5 Items */}
            <Row gutter={16}>
                <Col span={4}>
                    <Form.Item name="invoiceCategory" label="Invoice Category" rules={[{ required: true }]}>
                        <Select placeholder="Select category">
                            <Option value="Invoice">Invoice</Option>
                            <Option value="Estimate">Estimate</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[
                            { required: true, message: 'Required' },
                            { len: 10, message: 'Enter 10 digits' }
                        ]}

                    >
                        <Input maxLength={10}
                            onBlur={(e) => checkUser(e.target.value)}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Last Name">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
                <Col span={6}>
                    <Form.Item name="company" label="Company Name">
                        <Input maxLength={100} />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item name="gst" label="GST No.">
                        <Input maxLength={15} />
                    </Form.Item>
                </Col>
            </Row>

            {/* Second Row */}
            <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                <Col span={10}>
                    <Input prefix={<SearchOutlined />} ref={inputRef}
                        placeholder="Search product..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear />
                </Col>
                <Col span={7}>
                    <Form.Item name="salesEmployee" label="Sales Employee">
                        <Select placeholder="Select employee" showSearch options={dropdownOptions}
                            filterOption={(input, option) =>
                                typeof option?.label === 'string' &&
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={7}>
                    <Space>
                        <Button onClick={() => setDiscountModalVisible(true)}>Discounts</Button>
                        <Button
                            type="primary"
                            onClick={() => showCreateModal(cartData[0])}
                        > Add Job</Button>

                        <Button onClick={() => setPaymentModalVisible(true)}>Payment Details</Button>
                    </Space>
                </Col>
            </Row>

            {/* Table */}
            <div style={{ maxHeight: tableScrollY, overflowY: 'auto', marginBottom: 16 }}>
                <Table<CartItem>
                    sticky
                    columns={columns}
                    dataSource={cartData}
                    pagination={false}
                    rowKey="sr"
                    bordered
                    scroll={{ y: tableScrollY }}
                />
            </div>

            {/* Toggle Button */}
            <Row justify="space-between" align="middle" style={{ marginTop: 4 }}>
                <Col>
                    {!showBottomSection && (
                        <Title level={4} style={{ margin: 0 }}>
                            Total Amount: ₹{subtotal.toFixed(2)}
                        </Title>
                    )}
                </Col>
                <Col>
                    <Button
                        type="link"
                        onClick={() => setShowBottomSection(prev => !prev)}
                        icon={showBottomSection ? <CaretUpOutlined /> : <CaretDownOutlined />}
                    >
                        {showBottomSection ? 'Hide Details' : 'Show Details'}
                    </Button>
                </Col>
            </Row>
            


            {/* Bottom Section */}
            {showBottomSection && (
                <>
                    {/* Reference and Handover Section */}
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={8}>
                            <Form.Item name="refPhone" label="Reference Phone">
                                <Input maxLength={10} />
                            </Form.Item>
                            <Form.Item name="refName" label="Reference Name">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="handoverPhone" label="Phone  (Handover To Through)">
                                <Input maxLength={10} />
                            </Form.Item>
                            <Form.Item name="handoverName" label="Full Name">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Title level={4}>Total Amount: ₹{subtotal.toFixed(2)}</Title>
                        </Col>
                    </Row>
                </>
            )}
            {/* Footer Buttons */}
            <Row justify="end" gutter={16} style={{ marginTop: 24 }}>
                <Col>
                    <Button icon={<PrinterOutlined />}>Print</Button>
                </Col>
                <Col>
                    <Button danger onClick={() => form.resetFields()}>
                        Clear Cart
                    </Button>
                </Col>
                <Col>
                    <Button type="primary" htmlType="submit">Pay Now</Button>
                </Col>
            </Row>

            {/* Modals */}
            <Modal
                title="Discounts"
                open={discountModalVisible}
                onCancel={() => setDiscountModalVisible(false)}
                onOk={() => setDiscountModalVisible(false)}
            >
                <p>List of discounts and input fields go here...</p>
            </Modal>

            <AddJobModal
                visible={isModalVisible}
                onCancel={hideCreateModal}
                onCreate={handleCreate}
                isEditMode={isEditMode}
                initialValues={initialValues}
            />

            <Modal
                title="Payment Details"
                open={paymentModalVisible}
                onCancel={() => setPaymentModalVisible(false)}
                onOk={() => setPaymentModalVisible(false)}
            >
                <p>Payment modes and inputs to split payment types go here...</p>
            </Modal>
        </Form>
    </>
    );
};

export default InvoiceUI;
